import React, { useState, useEffect, useCallback } from 'react'
import { useDocumentsConfig } from '../contexts/DocumentsConfigContext'
import { graphql, compose } from 'react-apollo'
import {
  Container,
  Header,
  Body,
  Title,
  Line,
  Close,
  SmallText,
  Footer,
  Button,
  ErrorContainer,
  LoadingContainer,
} from '../styles/PrepareEsignModalElements'
import PrepareDocumentType from './components/PrepareDocumentType'
import FileUploader from '../../common/FileUploader'
import PrepareSigners from './components/PrepareSigners'
import LoadingBar from '../../common/LoadingBar'
import { getTemplate, createEsignature } from '../helpers/DocuSignUtil'
import UpsertDocumentBucket from '../queries/UpsertDocumentBucket'

export const DocSelectionTypes = {
  TEMPLATE: 'template',
  FILE: 'file',
}

const mapCustomFields = (templateFields, document) => {
  const obj = {}

  templateFields?.forEach((field) => {
    for (const parsedField of JSON.parse(document?.esignFields || '[]')) {
      if (parsedField?.name === field) {
        if (parsedField?.value) {
          obj[field] = parsedField?.value?.toString() || ''
        }

        break
      }
    }
  })

  return obj
}

const mapSigners = (templateRoles, document, guarantors) => {
  let arr = []

  templateRoles?.forEach((role) => {
    const signer = document?.esignSigners?.find(
      ({ role: signerRole }) => signerRole && signerRole === role,
    )

    if (signer) {
      arr.push({
        name: signer.name,
        email: signer.email,
        source_id: '',
        source_type: '',
        label: signer.label,
      })
    }
  })

  if (arr.length === 0) {
    arr = guarantors.map(({ name, email, rid }) => ({
      name,
      email,
      source_id: rid.toString(),
      source_type: 'Guarantor',
    }))
  }

  return arr
}

const PrepareDocuSignModal = ({
  document,
  guarantors,
  loanId,
  toggleModal,
  upsertDocumentBucket,
}) => {
  const [templateId, setTemplateId] = useState('')
  const [customFields, setCustomFields] = useState({})
  const [docSelection, setDocSelection] = useState('')
  const [customFileInfo, setCustomFileInfo] = useState(null)
  const [roles, setRoles] = useState([])
  const [signers, setSigners] = useState([])
  const [urlError, setUrlError] = useState('')
  const [btnLoading, setBtnLoading] = useState(false)
  const { docusignServiceBaseUrl } = useDocumentsConfig()

  const isTemplateType = docSelection === DocSelectionTypes.TEMPLATE
  const isFileType = docSelection === DocSelectionTypes.FILE

  useEffect(() => {
    const onSuccess = (
      responseTemplateId,
      responseCustomFields,
      responseRoles,
    ) => {
      setDocSelection(DocSelectionTypes.TEMPLATE)
      setTemplateId(responseTemplateId)
      setRoles(responseRoles)
      setCustomFields(mapCustomFields(responseCustomFields, document))
    }

    const onFailure = () => setDocSelection(DocSelectionTypes.FILE)

    getTemplate(
      docusignServiceBaseUrl,
      document?.title || '',
      onSuccess,
      onFailure,
    )
  }, [])

  useEffect(() => {
    if (isTemplateType) {
      setSigners(mapSigners(roles, document, guarantors))
    } else if (isFileType) {
      setSigners(mapSigners([], document, guarantors))
    }
  }, [docSelection, roles])

  const handleUploadDocument = useCallback(
    (file) => {
      const { name: filename } = file
      const reader = new FileReader()
      reader.onerror = () => console.error(reader.error)

      reader.onload = () => {
        const [, base64] = reader.result.split(',')
        setCustomFileInfo({ filename, base64 })
      }

      reader.readAsDataURL(file)
    },
    [customFileInfo],
  )

  const addSigner = useCallback(() => {
    const newSigners = signers.slice()

    newSigners.push({ name: '', email: '', source_id: '', source_type: '' })

    setSigners(newSigners)
  }, [signers])

  const editSigner = useCallback(
    (index, prop, e) => {
      const newSigners = signers.slice()

      newSigners[index][prop] = e?.target?.value || ''

      setSigners(newSigners)
    },
    [signers],
  )

  const removeSigner = useCallback(
    (index) => {
      if (signers.length == 1) return

      const newSigners = signers.slice()

      newSigners.splice(index, 1)

      setSigners(newSigners)
    },
    [signers],
  )

  const submitEsign = async () => {
    setUrlError('')
    setBtnLoading(true)

    const docuLink = await createEsignature(
      docusignServiceBaseUrl,
      signers,
      isTemplateType ? templateId : null,
      customFields,
      loanId,
      upsertDocumentBucket,
      document?.rid,
      isFileType ? customFileInfo : null,
    )

    if (docuLink) {
      window.location.href = docuLink
    } else {
      setUrlError('There was an error processing this request')
      setBtnLoading(false)
    }
  }

  const filledSigners = signers.filter((signer) => signer.name && signer.email)
  const isComplete =
    signers.length === filledSigners.length &&
    docSelection &&
    !btnLoading &&
    (!isFileType || customFileInfo)

  return (
    <Container>
      <Header>
        <Title>Prepare Document for E-Sign</Title>
        <Close onClick={toggleModal}>X</Close>
      </Header>
      <Body hasTemplate={templateId}>
        {docSelection ? (
          <>
            <SmallText size="14px" color="inherit">
              {templateId
                ? "We have a SmartBiz Template for this document, which should work for most cases. If the Template can't be used, select to use a Custom Document."
                : 'No standard template is available for this type of document. To use E-Sign, upload a document and manually prepare it to be sent out for signature.'}
            </SmallText>
            {templateId && (
              <>
                <br />
                <br />
                <SmallText size="14px" color="inherit">
                  <strong>Important: </strong>
                  Make sure the current lender on the loan is correct before
                  sending out any lender-specific documents, such as Landlord
                  Subordination.
                </SmallText>
                <PrepareDocumentType
                  isTemplateType={isTemplateType}
                  isFileType={isFileType}
                  setDocSelection={setDocSelection}
                  setCustomFileInfo={setCustomFileInfo}
                />
                <Line />
              </>
            )}
            {isFileType && (
              <FileUploader uploadDocument={handleUploadDocument} />
            )}
            <PrepareSigners
              isTemplateType={isTemplateType}
              signers={signers}
              filledSigners={filledSigners}
              editSigner={editSigner}
              removeSigner={removeSigner}
              addSigner={addSigner}
            />
            {urlError && <ErrorContainer>{urlError}</ErrorContainer>}
            <Footer>
              <Button disable={false} onClick={toggleModal}>
                Cancel
              </Button>
              <Button
                style={
                  isComplete
                    ? { color: 'white', backgroundColor: '#DC6A00' }
                    : { color: 'white' }
                }
                disable={!isComplete}
                onClick={() => submitEsign()}
              >
                Prepare for signing
              </Button>
            </Footer>
          </>
        ) : (
          <LoadingContainer>
            <LoadingBar />
          </LoadingContainer>
        )}
      </Body>
    </Container>
  )
}

const PrepareDocuSignModalWithQuery = compose(
  graphql(UpsertDocumentBucket, {
    name: 'upsertDocumentBucket',
  }),
)(PrepareDocuSignModal)

export default PrepareDocuSignModalWithQuery
